const U = (id, w = 600) =>
  `https://images.unsplash.com/${id}?crop=entropy&cs=srgb&fm=jpg&q=85&w=${w}`;

export const VARIANTS = [
  { value: "standard", label: "Standard" },
  { value: "foil", label: "Foil" },
  { value: "glow", label: "Glow-in-Dark" },
  { value: "ap", label: "Artist Proof" },
  { value: "test_print", label: "Test Print" },
];

export const CONDITIONS = ["Mint", "Near Mint", "Very Good", "Fair", "Poor"];

export const CATEGORIES = [
  { value: "concert", label: "Concert / Gig" },
  { value: "movie", label: "Movie" },
  { value: "theater", label: "Theater" },
  { value: "vintage", label: "Vintage" },
  { value: "ad", label: "Advertising" },
  { value: "anime", label: "Anime" },
  { value: "military", label: "Military" },
  { value: "car", label: "Car" },
  { value: "eclectic", label: "Eclectic" },
  { value: "adult", label: "Adult (18+)" },
];

export const categoryLabel = (v) => {
  const found = CATEGORIES.find((c) => c.value === v);
  if (found) return found.label;
  if (!v) return "Concert / Gig";
  return v.charAt(0).toUpperCase() + v.slice(1);
};

export const categoryBadge = (v) => {
  switch (v) {
    case "movie":
      return "bg-magenta/10 text-magenta border-magenta/40";
    case "theater":
      return "bg-gold/10 text-gold border-gold/40";
    case "vintage":
      return "bg-cyan/10 text-cyan border-cyan/40";
    case "ad":
      return "bg-glow/10 text-glow border-glow/40";
    case "anime":
      return "bg-fuchsia-500/10 text-fuchsia-300 border-fuchsia-500/40";
    case "military":
      return "bg-lime-600/10 text-lime-400 border-lime-600/40";
    case "car":
      return "bg-sky-500/10 text-sky-300 border-sky-500/40";
    case "eclectic":
      return "bg-orange-500/10 text-orange-300 border-orange-500/40";
    case "adult":
      return "bg-red-600/10 text-red-400 border-red-500/50";
    case "concert":
      return "bg-acid/10 text-acid border-acid/40";
    default:
      return "bg-slate-500/10 text-slate-300 border-slate-500/40"; // custom user categories
  }
};

export const COLOR_BADGE = {
  slate: "bg-slate-500/10 text-slate-300 border-slate-500/40",
  acid: "bg-acid/10 text-acid border-acid/40",
  cyan: "bg-cyan/10 text-cyan border-cyan/40",
  magenta: "bg-magenta/10 text-magenta border-magenta/40",
  gold: "bg-gold/10 text-gold border-gold/40",
  fuchsia: "bg-fuchsia-500/10 text-fuchsia-300 border-fuchsia-500/40",
  lime: "bg-lime-600/10 text-lime-400 border-lime-600/40",
  sky: "bg-sky-500/10 text-sky-300 border-sky-500/40",
  orange: "bg-orange-500/10 text-orange-300 border-orange-500/40",
  red: "bg-red-600/10 text-red-400 border-red-500/50",
};

export const isAdult = (poster) => poster?.adult === true || poster?.category === "adult";

export const mergeCategories = (custom = []) => {
  const known = new Set(CATEGORIES.map((c) => c.value));
  const extra = custom
    .filter((v) => v && !known.has(v))
    .map((v) => ({ value: v, label: categoryLabel(v) }));
  return [...CATEGORIES, ...extra];
};

export const variantBadge = (v) => {
  switch (v) {
    case "foil":
      return "bg-gold/10 text-gold border-gold/40 shadow-[0_0_12px_rgba(255,215,0,0.25)]";
    case "glow":
      return "bg-acid/10 text-acid border-acid/40 shadow-[0_0_12px_rgba(0,255,102,0.3)]";
    case "ap":
      return "bg-cyan/10 text-cyan border-cyan/40";
    case "test_print":
      return "bg-magenta/10 text-magenta border-magenta/40";
    default:
      return "bg-elevated text-muted-gray border-stroke";
  }
};

export const variantLabel = (v) => VARIANTS.find((x) => x.value === v)?.label || v;

export const CATEGORY_FALLBACKS = {
  concert: [
    U("photo-1747936919471-a8a959f7daff"),
    U("photo-1627782796583-46f04d38918a"),
    U("photo-1660692694114-9459092dd49f"),
    U("photo-1528491836309-55b4a140b78a"),
  ],
  movie: [
    U("photo-1616530940355-351fabd9524b"),
    U("photo-1626814026160-2237a95fc5a0"),
    U("photo-1571847140471-1d7766e825ea"),
    U("photo-1584445743187-cd8ba040349a"),
  ],
  theater: [
    U("photo-1662372850460-2b20d55a86f2"),
    U("photo-1572700432881-42c60fe8c869"),
  ],
  vintage: [
    U("photo-1729342036571-395d5caa6d86"),
    U("photo-1580130732478-4e339fb6836f"),
    U("photo-1584448097764-374f81551427"),
  ],
  ad: [
    U("photo-1580130718646-9f6942092ad3"),
    U("photo-1589824769965-d2bdab6dfe57"),
    U("photo-1709888245903-df72f60261f8"),
  ],
  anime: [
    U("photo-1769847780887-dc6f4380621e"),
    U("photo-1783788357929-a97642d0ef59"),
    U("photo-1769251300338-98d6104a46a4"),
  ],
  military: [
    U("photo-1580130857334-2f9b6d01d99d"),
    U("photo-1734547458530-10f069faa186"),
  ],
  car: [
    U("photo-1543796076-c8a565501995"),
    U("photo-1585897685768-409600fec957"),
    U("photo-1523828446771-151afb8374f1"),
  ],
  eclectic: [
    U("photo-1677411995856-cdea2aa6f479"),
    U("photo-1682056494999-f38db8c67e61"),
    U("photo-1602450069437-d52c62f52a71"),
  ],
  adult: [
    U("photo-1596387451000-edcd0d9f5aa5"),
    U("photo-1698732308311-98592dadedcc"),
    U("photo-1714171056117-f58040d6f5eb"),
  ],
};

export const fallbackFor = (category, index = 0) => {
  const arr = CATEGORY_FALLBACKS[category] || CATEGORY_FALLBACKS.concert;
  return arr[index % arr.length];
};

// Flat wall used for the global background collage (all categories mixed).
export const ALL_POSTERS = Object.values(CATEGORY_FALLBACKS).flat();

export const money = (n) =>
  n == null || n === "" ? "—" : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
